import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Clear as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import api from '../../services/api';
import { DocumentActivityLog } from '../../types/documentActivityLog';
import { formatAdminLocalDateTime } from '../../utils/DocumentUtils';

const DEFAULT_REFRESH_INTERVAL_MS = 30_000;

interface DocumentActivityTableProps {
  userId?: number;
  documentId?: number;
  compact?: boolean;
  /** When true, no fetch until userId is set (admin console requires a selected user). */
  requireUserId?: boolean;
  /** Poll for new entries without remounting (intended for the admin console tab). */
  autoRefresh?: boolean;
  refreshIntervalMs?: number;
}

type TimePreset = '24h' | '7d';

const ACTION_LABELS: Record<string, string> = {
  CATEGORY_CREATED: 'Category created',
  CATEGORY_UPDATED: 'Category updated',
  CATEGORY_DELETED: 'Category deleted',
  CATEGORY_MOVED: 'Category moved',
  CATEGORY_REORDERED: 'Category reordered',
  CONTENT_ADDED: 'Content added',
  CONTENT_UPDATED: 'Content updated',
  CONTENT_DELETED: 'Content deleted',
  CONTENT_ATTACHMENT_DELETED: 'Attachment deleted',
  CONTENT_MOVED: 'Content moved',
  CONTENT_BULK_ADDED: 'Bulk content added',
  OUTLINE_APPLIED: 'Outline applied',
  TREE_APPLIED_FROM_CONTENT: 'Structure from content',
  CONTENT_CATEGORY_SPLIT: 'Category split',
  CATEGORY_ABSORBED: 'Categories absorbed',
  MATCHING_CONTENT_ADDED: 'Matching content',
  MERGE_COMPARE_APPLIED: 'Merge compare',
  DOCUMENT_CREATED: 'Document created',
  DOCUMENT_RENAMED: 'Document renamed',
  DOCUMENT_DELETED: 'Document deleted',
  DOCUMENT_DUPLICATED: 'Document duplicated',
  DOCUMENT_MERGED: 'Document merged',
  DOCUMENT_EXPANDED: 'Document expanded',
  OPERATION_UNDONE: 'Undo',
  OPERATION_REDONE: 'Redo',
};

const EMPTY_CELL = '—';

const formatTimestamp = (value: string) => formatAdminLocalDateTime(value);

const DocumentActivityTable: React.FC<DocumentActivityTableProps> = ({
  userId,
  documentId,
  compact = false,
  requireUserId = false,
  autoRefresh = false,
  refreshIntervalMs = DEFAULT_REFRESH_INTERVAL_MS,
}) => {
  const [logs, setLogs] = useState<DocumentActivityLog[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(compact ? 15 : 25);
  const [totalElements, setTotalElements] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [timePreset, setTimePreset] = useState<TimePreset>('7d');
  const [loading, setLoading] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchLogs = useCallback(async (silent = false) => {
    if (requireUserId && userId == null) {
      return;
    }
    if (!silent) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    try {
      // Time window is resolved on the server (same clock as persisted timestamps).
      let url = `/admin/document-activity?page=${page}&size=${rowsPerPage}&preset=${encodeURIComponent(timePreset)}`;
      if (userId != null) {
        url += `&userId=${userId}`;
      }
      if (documentId != null) {
        url += `&documentId=${documentId}`;
      }
      if (searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }
      if (selectedAction) {
        url += `&action=${encodeURIComponent(selectedAction)}`;
      }
      const response = await api.get(url);
      setLogs(response.data.content || []);
      setTotalElements(response.data.totalElements || 0);
      setLastRefreshedAt(new Date());
    } catch (error) {
      console.error('Error fetching document activity:', error);
      if (!silent) {
        setLogs([]);
        setTotalElements(0);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  }, [page, rowsPerPage, userId, documentId, searchTerm, selectedAction, timePreset, requireUserId]);

  const fetchLogsRef = useRef(fetchLogs);
  fetchLogsRef.current = fetchLogs;

  useEffect(() => {
    if (requireUserId && userId == null) {
      setLogs([]);
      setTotalElements(0);
      setLoading(false);
      setLastRefreshedAt(null);
      return;
    }
    void fetchLogs(false);
  }, [fetchLogs, requireUserId, userId]);

  useEffect(() => {
    if (!autoRefresh || (requireUserId && userId == null)) {
      return undefined;
    }
    const interval = window.setInterval(() => {
      void fetchLogsRef.current(true);
    }, refreshIntervalMs);
    return () => window.clearInterval(interval);
  }, [autoRefresh, refreshIntervalMs, requireUserId, userId]);

  const handleTimePresetChange = (event: SelectChangeEvent<string>) => {
    setTimePreset(event.target.value as TimePreset);
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search summary, categories, document, action…"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, opacity: 0.6 }} />,
            endAdornment: searchTerm ? (
              <IconButton size="small" onClick={() => setSearchTerm('')} aria-label="Clear search">
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : undefined,
          }}
          sx={{ minWidth: 220, flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Time range</InputLabel>
          <Select label="Time range" value={timePreset} onChange={handleTimePresetChange}>
            <MenuItem value="24h">Last 24 hours</MenuItem>
            <MenuItem value="7d">Last 7 days</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Operation</InputLabel>
          <Select
            label="Operation"
            value={selectedAction}
            onChange={(e) => {
              setSelectedAction(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All operations</MenuItem>
            {Object.entries(ACTION_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {autoRefresh && lastRefreshedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {isRefreshing ? 'Refreshing…' : `Updated ${lastRefreshedAt.toLocaleString()}`}
          </Typography>
        )}
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              {!userId && <TableCell>User</TableCell>}
              <TableCell>Document</TableCell>
              <TableCell>Operation</TableCell>
              <TableCell>Target category</TableCell>
              <TableCell>Source category</TableCell>
              <TableCell>Summary</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={userId ? 6 : 7} align="center">
                  Loading…
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={userId ? 6 : 7} align="center">
                  No document changes recorded for this period.
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatTimestamp(log.timestamp)}
                  </TableCell>
                  {!userId && <TableCell>{log.username}</TableCell>}
                  <TableCell>{log.documentName}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={ACTION_LABELS[log.action] || log.action}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: compact ? 120 : 160 }}>
                      {log.targetCategoryName || EMPTY_CELL}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: compact ? 120 : 160 }}>
                      {log.sourceCategoryName || EMPTY_CELL}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={log.summary} placement="top-start">
                      <Typography variant="body2" noWrap sx={{ maxWidth: compact ? 280 : 480 }}>
                        {log.summary}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalElements}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[15, 25, 50]}
      />
    </Box>
  );
};

export default DocumentActivityTable;
